/**
 * Il markdown del manuale, reso in elementi — non in HTML iniettato.
 *
 * **Un lettore piccolo per un testo nostro.** I capitoli del manuale usano
 * titoli, paragrafi, elenchi, tabelle, grassetto, corsivo, codice e qualche
 * collegamento: quello che segue legge questo e niente altro. Una libreria
 * completa porterebbe con se' HTML grezzo, immagini remote e un `innerHTML`
 * da sorvegliare, per un testo che scriviamo noi e che non ha bisogno di
 * niente di tutto cio' (ADR-0001: nessuna dipendenza che non serve).
 *
 * **I titoli scalano di uno.** Il capitolo comincia con `#`, ma sulla pagina
 * il titolo di primo livello e' della schermata: il `#` del capitolo diventa
 * `h2`, e cosi' via. Chi ascolta la pagina sente una gerarchia sola.
 *
 * Cio' che il lettore non conosce resta un paragrafo di testo: non sparisce,
 * e non diventa un guasto.
 */
import { Fragment, type ReactNode } from 'react'

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'code'; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'rule' }

const CELLS = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())

/** Da testo a blocchi: una passata, riga per riga. */
export function parse(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i] ?? ''
    if (line.trim() === '') {
      i += 1
      continue
    }
    if (line.startsWith('```')) {
      const body: string[] = []
      i += 1
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) {
        body.push(lines[i] ?? '')
        i += 1
      }
      i += 1
      blocks.push({ kind: 'code', text: body.join('\n') })
      continue
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading !== null) {
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length, text: heading[2] ?? '' })
      i += 1
      continue
    }
    if (/^---+\s*$/.test(line)) {
      blocks.push({ kind: 'rule' })
      i += 1
      continue
    }
    if (line.startsWith('|')) {
      const head = CELLS(line)
      i += 1
      // La riga di separazione `|---|---|` non e' contenuto.
      if (i < lines.length && /^\|?\s*:?-{2,}/.test(lines[i] ?? '')) i += 1
      const rows: string[][] = []
      while (i < lines.length && (lines[i] ?? '').startsWith('|')) {
        rows.push(CELLS(lines[i] ?? ''))
        i += 1
      }
      blocks.push({ kind: 'table', head, rows })
      continue
    }
    if (line.startsWith('> ')) {
      const body: string[] = []
      while (i < lines.length && (lines[i] ?? '').startsWith('>')) {
        body.push((lines[i] ?? '').replace(/^>\s?/, ''))
        i += 1
      }
      blocks.push({ kind: 'quote', text: body.join(' ') })
      continue
    }
    const bullet = /^([-*]|\d+\.)\s+/.exec(line)
    if (bullet !== null) {
      const ordered = /^\d+\./.test(bullet[1] ?? '')
      const items: string[] = []
      while (i < lines.length) {
        const here = lines[i] ?? ''
        const start = /^([-*]|\d+\.)\s+(.*)$/.exec(here)
        if (start !== null) {
          items.push(start[2] ?? '')
          i += 1
          continue
        }
        // La riga rientrata continua la voce precedente.
        if (/^\s{2,}\S/.test(here) && items.length > 0) {
          items[items.length - 1] = `${items[items.length - 1]} ${here.trim()}`
          i += 1
          continue
        }
        break
      }
      blocks.push({ kind: 'list', ordered, items })
      continue
    }
    const body: string[] = [line]
    i += 1
    while (i < lines.length) {
      const here = lines[i] ?? ''
      if (here.trim() === '' || /^(#{1,6}\s|```|\||> |---|([-*]|\d+\.)\s)/.test(here)) break
      body.push(here)
      i += 1
    }
    blocks.push({ kind: 'paragraph', text: body.join(' ') })
  }
  return blocks
}

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g

/** Grassetto, corsivo, codice, collegamento: il resto e' testo. */
export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let n = 0
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > last) out.push(text.slice(last, at))
    const token = match[0]
    n += 1
    if (token.startsWith('**')) out.push(<strong key={n}>{token.slice(2, -2)}</strong>)
    else if (token.startsWith('`')) out.push(<code key={n}>{token.slice(1, -1)}</code>)
    else if (token.startsWith('*')) out.push(<em key={n}>{token.slice(1, -1)}</em>)
    else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token)
      // Solo collegamenti dentro la pagina o verso il web: un percorso di
      // file del repository non porta da nessuna parte sullo schermo.
      if (link !== null && /^(\/|#|https?:\/\/)/.test(link[2] ?? ''))
        out.push(
          <a key={n} href={link[2]}>
            {link[1]}
          </a>,
        )
      else out.push(link?.[1] ?? token)
    }
    last = at + token.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

export function Markdown({ source, shift = 1 }: { source: string; shift?: number }): JSX.Element {
  return (
    <>
      {parse(source).map((block, index) => {
        switch (block.kind) {
          case 'heading': {
            const level = Math.min(6, block.level + shift)
            const Tag = `h${level}` as 'h2'
            return <Tag key={index}>{inline(block.text)}</Tag>
          }
          case 'paragraph':
            return <p key={index}>{inline(block.text)}</p>
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul'
            return (
              <Tag key={index}>
                {block.items.map((item, k) => (
                  <li key={k}>{inline(item)}</li>
                ))}
              </Tag>
            )
          }
          case 'table':
            return (
              <table key={index}>
                <thead>
                  <tr>
                    {block.head.map((cell, k) => (
                      <th key={k}>{inline(cell)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, k) => (
                        <td key={k}>{inline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          case 'code':
            return (
              <pre key={index}>
                <code>{block.text}</code>
              </pre>
            )
          case 'quote':
            return <blockquote key={index}>{inline(block.text)}</blockquote>
          case 'rule':
            return <hr key={index} />
          default:
            return <Fragment key={index} />
        }
      })}
    </>
  )
}
